export interface ScrapedMenuItem {
  name: string;
  description: string;
  priceInGrosze: number | null;
  imageUrl?: string;
}

export interface ScrapedMenuCategory {
  name: string;
  items: ScrapedMenuItem[];
}

export interface ScrapedMenu {
  restaurantName: string;
  categories: ScrapedMenuCategory[];
  /** Logo image URL found in the page (Restaumatic imgix CDN). */
  logoUrl?: string;
  /** Human-readable address extracted from Schema.org JSON-LD. */
  address?: string;
  /** Email address found on the page (mailto link, JSON-LD or text pattern). */
  email?: string;
  /** Facebook profile URL found on the page. */
  facebookUrl?: string;
}

// ─── Proxy layer ───────────────────────────────────────────────────────────────

const PROXIES: Array<{
  buildUrl: (target: string) => string;
  extractText: (res: Response) => Promise<string>;
}> = [
  {
    buildUrl: (target) => `https://corsproxy.io/?url=${encodeURIComponent(target)}`,
    extractText: (res) => res.text(),
  },
  {
    buildUrl: (target) => `https://api.allorigins.win/get?url=${encodeURIComponent(target)}`,
    extractText: async (res) => {
      const json = await res.json();
      return json.contents as string;
    },
  },
  {
    buildUrl: (target) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(target)}`,
    extractText: (res) => res.text(),
  },
];

const PROXY_TIMEOUT_MS = 15_000;

async function fetchWithProxy(url: string): Promise<string> {
  const errors: string[] = [];

  for (const proxy of PROXIES) {
    const proxyUrl = proxy.buildUrl(url);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROXY_TIMEOUT_MS);

    try {
      const response = await fetch(proxyUrl, { signal: controller.signal });
      clearTimeout(timer);

      if (!response.ok) {
        errors.push(`${proxyUrl} → HTTP ${response.status}`);
        continue;
      }

      const text = await proxy.extractText(response);
      if (text && text.length > 200) return text;

      errors.push(`${proxyUrl} → pusta odpowiedź`);
    } catch (err: any) {
      clearTimeout(timer);
      errors.push(`${proxyUrl} → ${err?.message ?? 'błąd sieci'}`);
    }
  }

  throw new Error(
    'Wszystkie serwisy proxy są niedostępne. Spróbuj ponownie za chwilę.\n' +
      errors.join('\n')
  );
}

// ─── Main entry point ──────────────────────────────────────────────────────────

export const scrapeRestaumaticMenu = async (url: string): Promise<ScrapedMenu> => {
  const html = await fetchWithProxy(url.trim());
  return parseMenuFromHtml(html);
};

// ─── HTML parsing ──────────────────────────────────────────────────────────────

const parseMenuFromHtml = (html: string): ScrapedMenu => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Schema.org name is the most reliable source
  const ld = extractLdJson(doc);
  const restaurantName =
    ld?.name ||
    doc.querySelector('.restaurant-name, .site-name, h1.restaurant__name')?.textContent?.trim() ||
    doc.title.split('|')[0].split(' – ')[0].split(' - ')[0].trim() ||
    'Restauracja';

  // ── Categories ───────────────────────────────────────────────────────────────
  const categoryOrder: string[] = [];
  const categoryNames = new Map<string, string>();

  doc.querySelectorAll('[data-section-id]').forEach((el) => {
    const sectionId = el.getAttribute('data-section-id');
    if (!sectionId || categoryNames.has(sectionId)) return;

    const nameSpan = el.querySelector(
      'span:not(.dish-icons):not([data-field-id])'
    ) as HTMLElement | null;
    const rawName = (nameSpan?.textContent || el.textContent || '').trim();

    if (rawName && rawName.length < 120) {
      categoryNames.set(sectionId, rawName);
      categoryOrder.push(sectionId);
    }
  });

  // ── Items ────────────────────────────────────────────────────────────────────
  const itemsBySection = new Map<string, ScrapedMenuItem[]>();

  doc
    .querySelectorAll('li.m-list__item[data-menu-section-item-id], li[data-menu-section-item-id]')
    .forEach((el) => {
      const sectionId = el.getAttribute('data-menu-section-id');
      if (!sectionId) return;

      const titleEl = el.querySelector('h4.m-item__title, h4.restaurant-menu__dish-name, h4');
      let name = '';
      if (titleEl) {
        const clone = titleEl.cloneNode(true) as Element;
        clone.querySelectorAll('span.dish-icons, span[data-field-id]').forEach((s) => s.remove());
        name = clone.textContent?.trim() || '';
      }
      if (!name) return;

      const description =
        el
          .querySelector('.m-item__description .muted, .m-item__description span, .item-description')
          ?.textContent?.trim() || '';

      const priceText =
        el.querySelector('.add-button, button.add-button, .item-price, .price')?.textContent?.trim() || '';
      const priceInGrosze = parseHtmlPrice(priceText);

      // Extract product image — Restaumatic may lazy-load via data-src
      let imageUrl: string | undefined;
      const imgEl = el.querySelector('img') as HTMLImageElement | null;
      if (imgEl) {
        // Prefer data-src (lazy load), then src attribute value (not the resolved property)
        const rawSrc =
          imgEl.getAttribute('data-src') ||
          imgEl.getAttribute('data-lazy-src') ||
          imgEl.getAttribute('data-original') ||
          imgEl.getAttribute('src') ||
          '';
        if (rawSrc.startsWith('http') && !rawSrc.includes('placeholder') && !rawSrc.includes('blank.gif') && !rawSrc.includes('data:')) {
          imageUrl = rawSrc;
        }
      }

      if (!itemsBySection.has(sectionId)) itemsBySection.set(sectionId, []);
      itemsBySection.get(sectionId)!.push({ name, description, priceInGrosze, imageUrl });
    });

  // ── Assemble ─────────────────────────────────────────────────────────────────
  const categories: ScrapedMenuCategory[] = [];
  for (const sectionId of categoryOrder) {
    const items = itemsBySection.get(sectionId);
    if (items && items.length > 0) {
      categories.push({ name: categoryNames.get(sectionId)!, items });
    }
  }

  if (categories.length === 0) {
    throw new Error(
      'Nie znaleziono danych menu na tej stronie. ' +
        'Upewnij się, że link prowadzi do menu restauracji działającej w systemie Restaumatic.'
    );
  }

  return {
    restaurantName,
    categories,
    logoUrl: extractLogoUrl(doc),
    address: extractAddress(doc),
    email: extractEmail(doc),
    facebookUrl: extractFacebookUrl(doc),
  };
};

// ─── Logo & address helpers ───────────────────────────────────────────────────

/** Parse the first Restaurant/FoodEstablishment JSON-LD block from the page. */
const extractLdJson = (doc: Document): any => {
  const scripts = Array.from(doc.querySelectorAll('script[type="application/ld+json"]'));
  for (const script of scripts) {
    try {
      const data = JSON.parse(script.textContent || '');
      const type = data['@type'];
      if (type === 'Restaurant' || type === 'FoodEstablishment' || type === 'LocalBusiness') {
        return data;
      }
    } catch {
      // ignore malformed JSON
    }
  }
  return null;
};

const extractLogoUrl = (doc: Document): string | undefined => {
  // 1. Schema.org logo / image field
  const ld = extractLdJson(doc);
  if (ld?.logo) return typeof ld.logo === 'string' ? ld.logo : ld.logo?.url;
  if (ld?.image) return Array.isArray(ld.image) ? ld.image[0] : ld.image;

  // 2. <img> elements that look like a logo in the page header
  const candidates = Array.from(doc.querySelectorAll(
    'header img, .restaurant__logo img, .site-logo img, [class*="logo"] img, img[class*="logo"], img[alt*="logo"], img[alt*="Logo"]'
  ));
  for (const el of candidates) {
    const rawSrc =
      (el as HTMLImageElement).getAttribute('data-src') ||
      (el as HTMLImageElement).getAttribute('data-lazy-src') ||
      (el as HTMLImageElement).getAttribute('src') ||
      '';
    if (rawSrc && rawSrc.startsWith('http') && !rawSrc.includes('placeholder') && !rawSrc.includes('data:')) return rawSrc;
  }

  // 3. Any imgix /logo/ URL anywhere in the raw HTML
  const match = doc.documentElement.innerHTML.match(
    /https:\/\/restaumatic-production\.imgix\.net\/uploads\/restaurants\/\d+\/logo\/[^"'\s>]+/
  );
  return match ? match[0] : undefined;
};

const extractAddress = (doc: Document): string | undefined => {
  // 1. Schema.org PostalAddress
  const ld = extractLdJson(doc);
  if (ld?.address) {
    const a = ld.address;
    const parts = [a.streetAddress, a.postalCode, a.addressLocality].filter(Boolean);
    if (parts.length >= 2) return parts.join(', ');
  }

  // 2. <address> element in footer
  const addressEl = doc.querySelector('footer address, .restaurant__address, [class*="address"]');
  const text = addressEl?.textContent?.replace(/\s+/g, ' ').trim();
  if (text && text.length > 5 && text.length < 200) return text;

  return undefined;
};

const extractFacebookUrl = (doc: Document): string | undefined => {
  const FB_RE = /^https?:\/\/(www\.)?facebook\.com\/(?!sharer|share|dialog|plugins|login|home|pages\/create)[^"'\s>?#]+/;

  // 1. Schema.org sameAs array
  const ld = extractLdJson(doc);
  const sameAs: string[] = Array.isArray(ld?.sameAs) ? ld.sameAs : ld?.sameAs ? [ld.sameAs] : [];
  const fromLd = sameAs.find((u) => FB_RE.test(u));
  if (fromLd) return fromLd;

  // 2. <a href="https://www.facebook.com/..."> links on the page
  const links = Array.from(doc.querySelectorAll('a[href*="facebook.com"]')) as HTMLAnchorElement[];
  for (const a of links) {
    const href = a.getAttribute('href') || '';
    if (FB_RE.test(href)) return href.split('?')[0].replace(/\/$/, '');
  }

  return undefined;
};

const extractEmail = (doc: Document): string | undefined => {
  // 1. Schema.org email field
  const ld = extractLdJson(doc);
  if (ld?.email && typeof ld.email === 'string' && ld.email.includes('@')) return ld.email.trim();

  // 2. mailto: links (skip social/placeholder addresses)
  const mailtoLinks = Array.from(doc.querySelectorAll('a[href^="mailto:"]'));
  for (const el of mailtoLinks) {
    const href = (el as HTMLAnchorElement).href;
    const addr = href.replace('mailto:', '').split('?')[0].trim().toLowerCase();
    if (addr.includes('@') && !addr.includes('example') && !addr.includes('your-email')) {
      return addr;
    }
  }

  // 3. Regex scan of visible text — catches emails written as plain text
  const bodyText = doc.body?.textContent || '';
  const emailRegex = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;
  const matches = bodyText.match(emailRegex);
  if (matches) {
    const filtered = matches.find(
      (m) => !m.includes('example') && !m.includes('your-email') && !m.endsWith('.png') && !m.endsWith('.jpg')
    );
    if (filtered) return filtered.toLowerCase();
  }

  return undefined;
};

// ─── Logo fetch helper (exported for use in modal) ───────────────────────────

/**
 * Fetches a logo image and returns it as a Blob.
 * Tries the URL directly first (CDN usually allows CORS),
 * then falls back to corsproxy.io.
 */
export const fetchLogoAsBlob = async (logoUrl: string): Promise<Blob> => {
  // Direct fetch (works for imgix CDN which sets CORS headers)
  try {
    const res = await fetch(logoUrl);
    if (res.ok) {
      const blob = await res.blob();
      if (blob.size > 0) return blob;
    }
  } catch {
    // fall through to proxy
  }

  // Proxy fallback
  const proxyUrl = `https://corsproxy.io/?url=${encodeURIComponent(logoUrl)}`;
  const res = await fetch(proxyUrl);
  if (!res.ok) throw new Error(`Nie udało się pobrać logo (HTTP ${res.status})`);
  return res.blob();
};

// ─── Price helper ─────────────────────────────────────────────────────────────

/**
 * Parses price strings from Restaumatic HTML buttons.
 * Examples: "od 27,00 zł", "20,00 zł", "20 zł"
 * For "od X" prices (multi-variant items) returns the minimum price shown.
 * Returns price in grosze or null.
 */
const parseHtmlPrice = (text: string): number | null => {
  if (!text) return null;
  const cleaned = text.replace(/od\s*/i, '').trim();
  const match = cleaned.match(/(\d+)[,.](\d{1,2})/);
  if (!match) {
    const intMatch = cleaned.match(/(\d+)/);
    if (!intMatch) return null;
    return parseInt(intMatch[1]) * 100;
  }
  const zloty = parseInt(match[1]);
  const groszeStr = match[2].padEnd(2, '0');
  return zloty * 100 + parseInt(groszeStr);
};
